import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export type PopupSize    = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
export type PopupVariant = 'primary' | 'secondary' | 'tertiary';
export type PopupColor   = 'amarante' | 'moonstone' | 'rose' | 'mint'
                         | 'semantic-red' | 'semantic-yellow' | 'semantic-green';

@Component({
  selector: 'app-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './popup.component.html',
  styleUrls: ['./popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PopupComponent implements OnChanges {

  constructor(private cdr: ChangeDetectorRef) {}

  // ── Visibilité ─────────────────────────────────────────────────────────────

  /** Ouvre ou ferme le popup */
  @Input() isOpen = false;

  // ── Header ─────────────────────────────────────────────────────────────────

  /** Titre affiché dans le header */
  @Input() title = '';

  /**
   * Variante visuelle du header :
   * - 'primary'   → fond plein couleur, titre blanc
   * - 'secondary' → fond léger couleur, titre couleur, bordure basse couleur
   * - 'tertiary'  → fond blanc, titre couleur (pas de couleur de fond)
   */
  @Input() variant: PopupVariant = 'tertiary';

  /** Couleur du thème du header */
  @Input() color: PopupColor = 'amarante';

  /** Afficher une icône à gauche du titre (contenu via slot="header-icon-left") */
  @Input() addIconLeft = false;

  /** Afficher une icône à droite du titre (contenu via slot="header-icon-right") */
  @Input() addIconRight = false;

  // ── Dimensions ─────────────────────────────────────────────────────────────

  /**
   * Largeur max du popup :
   * xsmall=320px | small=400px | medium=480px | large=600px | xlarge=720px
   */
  @Input() size: PopupSize = 'medium';

  // ── Output ─────────────────────────────────────────────────────────────────

  /** Émis au clic sur ✕ ou à la touche Escape */
  @Output() closed = new EventEmitter<void>();

  // ── État animation ─────────────────────────────────────────────────────────

  isVisible      = false;
  isAnimatingOut = false;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.isVisible      = true;
        this.isAnimatingOut = false;
      } else {
        this.triggerClose();
      }
    }
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  close(): void {
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.close();
  }

  // ── Classes BEM ────────────────────────────────────────────────────────────

  get popupClasses(): Record<string, boolean> {
    return {
      'popup':                    true,
      [`popup--${this.size}`]:    true,
      'popup--out':               this.isAnimatingOut,
    };
  }

  get headerClasses(): Record<string, boolean> {
    return {
      'popup__header':                       true,
      [`popup__header--${this.variant}`]:    true,
      [`popup__header--${this.color}`]:      true,
    };
  }

  // ── Privé ──────────────────────────────────────────────────────────────────

  private triggerClose(): void {
    if (!this.isVisible) return;
    this.isAnimatingOut = true;
    setTimeout(() => {
      this.isVisible      = false;
      this.isAnimatingOut = false;
      this.cdr.detectChanges();   // zoneless : force le rendu après le setTimeout (markForCheck ne suffit pas)
    }, 220);
  }
}